import { formatDate } from '@codes/utils';

import type { TFunction } from 'i18next';

/**
 * 프롬프트 내용을 기반으로 적절한 이름을 생성하는 함수
 */
export const generatePromptName = (contents: string[], stereo: 'system' | 'user', fallbackPrefix: string): string => {
    if (!contents || contents.length === 0) {
        return `${fallbackPrefix} ${formatDate(new Date().getTime(), 'YYMMDD_HHmm')}`;
    }

    // 첫 번째 내용에서 의미있는 텍스트 추출
    const firstContent = contents[0]?.trim() || '';

    // 특수문자나 마크다운 제거
    let cleanText = firstContent
        .replace(/^[#*\-+>\s]+/, '') // 마크다운 문법 제거
        .replace(/[^\w\s가-힣]/g, ' ') // 특수문자 제거 (한글, 영문, 숫자, 공백만 유지)
        .trim();

    // 길이 제한 (최대 30자)
    if (cleanText.length > 30) {
        cleanText = cleanText.substring(0, 30).trim() + '...';
    }

    // 의미있는 텍스트가 있으면 사용, 없으면 기본값
    if (cleanText.length > 0) {
        return `${cleanText}`;
    }

    return `${fallbackPrefix} ${formatDate(new Date().getTime(), 'YYMMDD_HHmm')}`;
};

/**
 * 프롬프트 내용을 문자열로 변환하는 함수
 */
export const contentsToString = (contents: string | string[] | undefined): string => {
    if (!contents) {
        return '';
    }
    if (typeof contents === 'string') {
        return contents;
    }
    if (Array.isArray(contents)) {
        return contents.join('\n');
    }
    return '';
};

/**
 * 온도 표시값을 API 값으로 변환하는 함수
 */
export const getTemperatureDisplayValue = (apiValue: number | undefined): number => {
    if (apiValue === undefined || apiValue === null) {
        return 1.0;
    }
    return Math.round((apiValue / 100) * 10) / 10;
};

/**
 * 온도 표시값을 포맷팅하는 함수
 */
export const formatTemperatureDisplay = (displayValue: number, t: TFunction): string => {
    return displayValue === 1.0 ? t('agent.form.advanced.settings.temperature.maxLabel') : displayValue.toFixed(1);
};

/**
 * 숫자를 포맷팅하는 함수
 */
export const formatNumber = (num: number) => num.toLocaleString();
